const STORAGE_KEY = "planmat:pending_redirect";
const CLAIM_KEY = "planmat:pending_redirect:claimed";
const TTL_MS = 10 * 60 * 1000;

type PendingRedirect = {
  path: string;
  savedAt: number;
};

/**
 * A saved path must be relative to this origin: no scheme/host of its own
 * (open-redirect) and no `//`/`/\` prefix (browsers treat those as
 * protocol-relative URLs pointing at a different host).
 */
const isSafeRelativePath = (path: string): boolean => {
  if (!path || !path.startsWith("/")) return false;
  if (path.startsWith("//") || path.startsWith("/\\")) return false;
  return true;
};

export const savePendingRedirect = (path: string): void => {
  if (!isSafeRelativePath(path)) return;

  const entry: PendingRedirect = { path, savedAt: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage unavailable (private mode / quota) -- best effort only,
    // the user just lands on home after login instead of the deep link.
  }
};

/** Reads and clears the pending redirect, if any, still within its
 * 10-minute window and still a safe relative path. */
export const consumePendingRedirect = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    localStorage.removeItem(STORAGE_KEY);

    const entry: PendingRedirect = JSON.parse(raw);
    if (Date.now() - entry.savedAt > TTL_MS) return null;
    if (!isSafeRelativePath(entry.path)) return null;
    return entry.path;
  } catch {
    return null;
  }
};

/** Same validity check as `consumePendingRedirect`, but without clearing
 * the entry -- for callers that only need to know it exists (e.g. to mark
 * it claimed) without being the one navigating to it. */
export const peekPendingRedirect = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const entry: PendingRedirect = JSON.parse(raw);
    if (Date.now() - entry.savedAt > TTL_MS) return null;
    if (!isSafeRelativePath(entry.path)) return null;
    return entry.path;
  } catch {
    return null;
  }
};

/**
 * Duplicate-tab conflict resolution: when a login broadcasts to every
 * expired tab at once, only the first tab to claim a given pending path
 * should navigate there; the rest fall back to home instead of all jumping
 * to the same deep link. Returns true if this call is the one that claimed
 * it (i.e. no one had claimed this exact path yet).
 */
export const claimPendingRedirect = (path: string): boolean => {
  try {
    if (localStorage.getItem(CLAIM_KEY) === path) return false;
    localStorage.setItem(CLAIM_KEY, path);
    return true;
  } catch {
    // Fail open: without localStorage there's no way to coordinate across
    // tabs anyway, so don't block navigation over it.
    return true;
  }
};

